import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { AI_MODELS } from '../../constants/aiModels';
import './PaymentSuccess.css';

// 진입 경로(BillingSuccess, PaymentWidgetSuccess)가 항상 planId를 state로 전달한다.
// planName이 오면 그대로 쓰고, 없으면 이 매핑으로 표시한다.
const PLAN_NAMES = {
  pro_1month: '왓퀴즈 PRO 1개월',
  pro_3month: '왓퀴즈 PRO 3개월',
  pro_6month: '왓퀴즈 PRO 6개월',
  pro_12month: '왓퀴즈 PRO 12개월',
  monthly: '왓퀴즈 PRO 월간',
  pro_monthly: '왓퀴즈 PRO 월간',
  MONTHLY: '왓퀴즈 PRO 월간',
  yearly: '왓퀴즈 PRO 연간',
  pro_yearly: '왓퀴즈 PRO 연간',
  YEARLY: '왓퀴즈 PRO 연간'
};

const getPlanDisplayName = (paymentData) => {
  if (paymentData.planName) return paymentData.planName;
  return PLAN_NAMES[paymentData.planId] || '왓퀴즈 PRO';
};

export const getDocumentsPaymentKey = (paymentData) => {
  const key = String(paymentData?.paymentKey || '');
  if (!key || key === 'duplicate_prevented') return '';
  if (!(Number(paymentData?.amount) > 0)) return '';
  return key;
};

// 브랜드 파랑 계열 + 포인트 1색 (색상 절제 원칙)
const CONFETTI_COLORS = ['#3182f6', '#60a5fa', '#93c5fd', '#dbeafe', '#ffd66b'];
const CONFETTI_COUNT = 36;

const createConfettiPieces = () =>
  Array.from({ length: CONFETTI_COUNT }, (_, i) => ({
    left: Math.random() * 100,
    delay: Math.random() * 0.8,
    duration: 1.8 + Math.random() * 0.9,
    size: 6 + Math.random() * 5,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length]
  }));

const formatEndDate = (endDate) => {
  if (!endDate) return null;
  const date = new Date(endDate);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
};

const BENEFIT_ICONS = {
  ai: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z" fill="currentColor" />
      <path d="M18.5 15l.95 2.55L22 18.5l-2.55.95L18.5 22l-.95-2.55L15 18.5l2.55-.95L18.5 15z" fill="currentColor" opacity="0.55" />
    </svg>
  ),
  download: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 4v10m0 0l-4-4m4 4l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 19h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  unlimited: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8.5 15.5c-1.933 0-3.5-1.567-3.5-3.5s1.567-3.5 3.5-3.5c2.5 0 4.5 7 7 7 1.933 0 3.5-1.567 3.5-3.5s-1.567-3.5-3.5-3.5c-2.5 0-4.5 7-7 7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
};

const PRO_BENEFITS = [
  {
    icon: BENEFIT_ICONS.ai,
    title: '최신 AI 3종',
    description: `${AI_MODELS.gpt.latest}, ${AI_MODELS.claude.latest}, ${AI_MODELS.gemini.latest}`
  },
  {
    icon: BENEFIT_ICONS.download,
    title: 'PPT·학습지 다운로드',
    description: '10가지 테마 PPT와 이미지 학습지'
  },
  {
    icon: BENEFIT_ICONS.unlimited,
    title: '무제한 사용',
    description: '모든 AI 기능을 제한 없이'
  }
];

const PaymentSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { fetchSubscriptionStatus, subscriptionStatus } = useSubscription();
  const paymentData = location.state;
  const [showConfetti, setShowConfetti] = useState(true);
  // 조각 데이터는 마운트 시 1회만 생성 (리렌더 시 랜덤 재계산 방지)
  const [confettiPieces] = useState(createConfettiPieces);

  useEffect(() => {
    // 결제 데이터 없이 직접 진입하면 홈으로
    if (!paymentData) {
      navigate('/');
      return;
    }

    if (!currentUser) {
      navigate('/login');
      return;
    }

    // 결제 직후 구독 상태 강제 갱신 (캐시 무시) → 만료일(endDate)도 이때 채워진다
    fetchSubscriptionStatus(true).catch((error) => {
      console.error('구독 상태 업데이트 실패:', error);
    });

    document.title = '결제 완료 - 왓퀴즈';

    // 모든 confetti가 낙하를 마친 뒤(최대 delay 0.8s + duration 2.7s) 컨테이너 제거
    const timer = setTimeout(() => setShowConfetti(false), 4000);
    return () => clearTimeout(timer);
  }, [paymentData, currentUser, navigate, fetchSubscriptionStatus]);

  if (!paymentData) {
    return null;
  }

  const endDateText = formatEndDate(subscriptionStatus?.endDate);
  const documentsPaymentKey = getDocumentsPaymentKey(paymentData);

  return (
    <div className="paysuccess-page">
      {showConfetti && (
        <div className="paysuccess-confetti-container" aria-hidden="true">
          {confettiPieces.map((piece, i) => (
            <div
              key={i}
              className="paysuccess-confetti"
              style={{
                left: `${piece.left}%`,
                width: `${piece.size}px`,
                height: `${piece.size}px`,
                backgroundColor: piece.color,
                animationDelay: `${piece.delay}s`,
                animationDuration: `${piece.duration}s`
              }}
            />
          ))}
        </div>
      )}

      <div className="paysuccess-content">
        <div className="paysuccess-hero">
          <div className="paysuccess-check-circle">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                className="paysuccess-check-icon"
                d="M20 6L9 17L4 12"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h1 className="paysuccess-title">PRO 시작을 축하해요</h1>
          <p className="paysuccess-subtitle">
            {endDateText ? (
              <>
                <strong>{endDateText}</strong>까지 모든 AI 기능을
                <br />
                마음껏 쓸 수 있어요
              </>
            ) : (
              <>이제 모든 AI 기능을 마음껏 쓸 수 있어요</>
            )}
          </p>
        </div>

        <div className="paysuccess-receipt">
          <div className="paysuccess-receipt-row">
            <span className="paysuccess-receipt-label">이용권</span>
            <span className="paysuccess-receipt-value">{getPlanDisplayName(paymentData)}</span>
          </div>
          <div className="paysuccess-receipt-row">
            <span className="paysuccess-receipt-label">결제 금액</span>
            <span className="paysuccess-receipt-value paysuccess-receipt-value--amount">
              {paymentData.amount?.toLocaleString()}원
            </span>
          </div>
          <div className="paysuccess-receipt-row">
            <span className="paysuccess-receipt-label">주문번호</span>
            <span className="paysuccess-receipt-value paysuccess-receipt-value--order">
              {paymentData.orderId || paymentData.orderNo}
            </span>
          </div>
        </div>

        {documentsPaymentKey && (
          <button
            className="paysuccess-docs"
            onClick={() => navigate(`/subscription/documents/${encodeURIComponent(documentsPaymentKey)}`)}
            type="button"
          >
            <span>
              <strong>증빙 서류 받기</strong>
              <small>거래명세서·영수증은 메일로도 보내드려요</small>
            </span>
            <span aria-hidden="true">›</span>
          </button>
        )}

        <div className="paysuccess-benefits">
          <h2 className="paysuccess-benefits-title">이제 이런 걸 할 수 있어요</h2>
          <div className="paysuccess-benefits-grid">
            {PRO_BENEFITS.map((benefit) => (
              <div key={benefit.title} className="paysuccess-benefit">
                <div className="paysuccess-benefit-icon">{benefit.icon}</div>
                <div className="paysuccess-benefit-text">
                  <h3 className="paysuccess-benefit-title">{benefit.title}</h3>
                  <p className="paysuccess-benefit-desc">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="paysuccess-actions">
          <button className="paysuccess-cta" onClick={() => navigate('/home')}>
            AI 기능 사용하러 가기
          </button>
          <button className="paysuccess-manage-link" onClick={() => navigate('/subscription')}>
            구독 관리
          </button>
        </div>

        <p className="paysuccess-support">
          궁금한 점이 있으면{' '}
          <a href="mailto:angel@plactical.com">angel@plactical.com</a>으로 연락해주세요
        </p>
      </div>
    </div>
  );
};

export default PaymentSuccess;
